/**
 * SORUMLULUK MERKEZİ SEÇİCİ — fatura, teslimat belgesi, transfer, personel.
 *
 * Kârlılık merkez bazında ölçülüyor, bu yüzden her İŞLEM kaydı bir merkez
 * taşımalı. Seçici dört ekranda birden kullanıldığı için tek bileşen: merkez
 * listesinin nasıl çekildiği ve tek merkezli şirkette ne yapıldığı tek yerde
 * kalsın.
 *
 * TEK MERKEZ VARSA seçim kutusu YOK, salt okunur rozet var: bugünkü kullanıcı
 * tek merkezle çalışıyor ve her fatura girişinde aynı şeyi seçtirmek boş bir
 * tıklama olurdu. Kayıt yine merkez alır — sunucu da tek merkezi kendisi
 * çözüyor (`routes/sorumluluk.py` → `merkez_coz`), yani ekran kapalı olsa bile
 * merkezsiz kayıt oluşmaz. Şirkete ikinci merkez eklendiği an seçim kutusu
 * kendiliğinden belirir.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target } from 'lucide-react';

// Merkezsiz seçeneğin değeri. Radix Select boş string kabul etmiyor
// (SelectItem value="" çalışmıyor), bu yüzden bir nöbetçi değer kullanılıyor.
const YOK = '__yok__';

// `etiketClass`/`tetikClass`: fatura girişi gibi kendi kompakt grid stili olan
// ekranlar seçiciyi kendi hizasına oturtabilsin diye. Verilmezse form stili.
export default function MerkezSecici({ value, onChange, label = 'Sorumluluk Merkezi',
                                       zorunlu = false, className = '',
                                       etiketClass = '', tetikClass = '' }) {
  const [merkezler, setMerkezler] = useState([]);
  const [yuklendi, setYuklendi] = useState(false);

  useEffect(() => {
    let iptal = false;
    api.get('/sorumluluk-merkezleri')
      .then(r => { if (!iptal) { setMerkezler(r.data || []); setYuklendi(true); } })
      .catch(() => { if (!iptal) setYuklendi(true); });
    return () => { iptal = true; };
  }, []);

  // Tek merkez varsa seçimi ÜSTE bildir: kaydı yapan ekran merkezi elinde
  // tutsun, sunucunun çözmesine bel bağlamasın (fatura düzenlemede kısmi
  // güncelleme merkezi boşaltabilirdi).
  useEffect(() => {
    if (yuklendi && merkezler.length === 1 && !value) onChange?.(merkezler[0].id);
  }, [yuklendi, merkezler, value, onChange]);

  if (!yuklendi || merkezler.length === 0) return null;

  const secili = merkezler.find(m => m.id === value);

  const Etiket = ({ children }) => (etiketClass
    ? <p className={etiketClass}>{children}</p>
    : <Label className="text-xs uppercase text-[#8a7e70]">{children}</Label>);

  if (merkezler.length === 1) {
    return (
      <div className={etiketClass ? `min-w-0 ${className}` : `space-y-2 ${className}`}>
        <Etiket>{label}</Etiket>
        <div className={`flex items-center gap-2 px-3 rounded-md border border-[#d0c4b0] bg-[#f5f0e8] text-[#3F3E39] truncate ${tetikClass || 'h-10 text-sm'}`}
             title={merkezler[0].name} data-testid="merkez-tek">
          <Target size={14} className="text-[#8a7e70] shrink-0" />
          <span className="truncate">{merkezler[0].name}</span>
          <span className="ml-auto text-xs text-[#8a7e70] shrink-0">{merkezler[0].para_birimi}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={etiketClass ? `min-w-0 ${className}` : `space-y-2 ${className}`}>
      <Etiket>{label}{zorunlu && <span className="text-[#a34b3f]"> *</span>}</Etiket>
      <Select value={value || YOK} onValueChange={(v) => onChange?.(v === YOK ? '' : v)}>
        <SelectTrigger className={tetikClass || 'bg-white border-[#d0c4b0]'} data-testid="merkez-secici">
          <SelectValue placeholder="Merkez seçin">
            {secili ? `${secili.name} (${secili.para_birimi})` : 'Merkez seçin'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {!zorunlu && <SelectItem value={YOK}>— Merkez seçilmedi —</SelectItem>}
          {merkezler.map(m => (
            <SelectItem key={m.id} value={m.id}>
              {m.name} ({m.para_birimi})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
